import React from "react";
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  InputLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { FontWeight } from "../../globalStyles/emotion";
import {
  formatCentsAsCurrency,
  parseCurrencyAmountToCents,
} from "../../utils/formatCurrency";

const parseNonNegativeIntegerInput = (value) => {
  if (value === "") return null;
  if (!/^\d+$/.test(value)) return null;

  const count = Number(value);
  if (!Number.isInteger(count) || count < 0) return null;

  return count;
};

const RidePassSection = ({
  passShown,
  setPassShown,
  selectedReasons,
  setSelectedReasons,
  uncollectedReasons,
  passName,
}) => {
  const handleToggle = (event) => {
    setPassShown(event.target.checked);
    setSelectedReasons([]);
  };

  const toggleReason = (slug) => {
    setSelectedReasons((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  return (
    <div
      style={{
        marginTop: 20,
        borderTop: "1px solid lightgrey",
        paddingTop: 15,
      }}
    >
      <InputLabel style={{ marginBottom: 10 }}>{passName || "Ride Pass"}</InputLabel>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography>Did the rider show you a {passName || "ride pass"}?</Typography>
        <Switch checked={passShown} onChange={handleToggle} />
      </div>

      {!passShown && uncollectedReasons && uncollectedReasons.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <Typography>Please provide the reason:</Typography>
          <FormGroup>
            {uncollectedReasons.map((reason) => (
              <FormControlLabel
                key={reason.slug}
                control={
                  <Checkbox
                    checked={selectedReasons.includes(reason.slug)}
                    onChange={() => toggleReason(reason.slug)}
                  />
                }
                label={reason.label}
              />
            ))}
          </FormGroup>
        </div>
      )}
    </div>
  );
};

const CashVoucherSection = ({
  paymentCollected,
  setPaymentCollected,
  cashAmountCents,
  setCashAmountCents,
  voucherAmountCents,
  setVoucherAmountCents,
  voucherCount,
  setVoucherCount,
  selectedReasons,
  setSelectedReasons,
  inPersonPaymentOptions,
  uncollectedReasons,
  costCenter,
  riderFareCents = null,
}) => {
  const handleToggleCollected = (event) => {
    setPaymentCollected(event.target.checked);
    setCashAmountCents(null);
    setVoucherAmountCents(null);
    setVoucherCount(null);
    setSelectedReasons([]);
  };

  const toggleReason = (slug) => {
    setSelectedReasons((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  const showCashInput = inPersonPaymentOptions.inPersonCashEnabled;
  const showVoucherInput = inPersonPaymentOptions.inPersonVouchersEnabled;

  const isVoucherOnly =
    inPersonPaymentOptions.inPersonVouchersEnabled &&
    !inPersonPaymentOptions.inPersonCashEnabled;
  const voucherName = isVoucherOnly && costCenter?.name ? costCenter.name : "Voucher";
  const voucherLabel = `${voucherName} Total Amount`;

  return (
    <div
      style={{
        marginTop: 20,
        borderTop: "1px solid lightgrey",
        paddingTop: 15,
      }}
    >
      <InputLabel style={{ marginBottom: 10 }}>
        In-person Payment Collected
      </InputLabel>

      {riderFareCents != null && (
        <Typography variant="body2" style={{ marginBottom: 10, fontWeight: 600 }}>
          {formatCentsAsCurrency(riderFareCents)} fare is due
        </Typography>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography>Did the rider provide in-person payment?</Typography>
        <Switch checked={paymentCollected} onChange={handleToggleCollected} />
      </div>

      {paymentCollected ? (
        <div style={{ marginTop: 10 }}>
          <Typography>
            Please record what was collected from the rider:
          </Typography>

          <div style={{ marginTop: 10 }}>
            {showCashInput && (
              <div style={{ marginBottom: 10 }}>
                <InputLabel required style={{ marginBottom: 10 }}>
                  Cash Amount
                </InputLabel>
                <TextField
                  type="number"
                  required
                  inputProps={{ min: 0, step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  value={cashAmountCents !== null ? cashAmountCents / 100 : ""}
                  onChange={(e) =>
                    setCashAmountCents(
                      parseCurrencyAmountToCents(e.target.value)
                    )
                  }
                />
              </div>
            )}
            {showVoucherInput && (
              <>
                <div style={{ marginBottom: 10 }}>
                  <InputLabel required style={{ marginBottom: 10 }}>
                    {voucherLabel}
                  </InputLabel>
                  <TextField
                    type="number"
                    required
                    inputProps={{ min: 0, step: "0.01" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    value={
                      voucherAmountCents !== null
                        ? voucherAmountCents / 100
                        : ""
                    }
                    onChange={(e) =>
                      setVoucherAmountCents(
                        parseCurrencyAmountToCents(e.target.value)
                      )
                    }
                  />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <InputLabel required style={{ marginBottom: 10 }}>
                    # of {voucherName}s Collected
                  </InputLabel>
                  <TextField
                    type="number"
                    required
                    inputProps={{ min: 0, step: 1 }}
                    value={voucherCount ?? ""}
                    onChange={(e) =>
                      setVoucherCount(
                        parseNonNegativeIntegerInput(e.target.value)
                      )
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <>
          {uncollectedReasons && uncollectedReasons.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <Typography>Please provide the reason:</Typography>
              <FormGroup>
                {uncollectedReasons.map((reason) => (
                  <FormControlLabel
                    key={reason.slug}
                    control={
                      <Checkbox
                        checked={selectedReasons.includes(reason.slug)}
                        onChange={() => toggleReason(reason.slug)}
                      />
                    }
                    label={reason.label}
                  />
                ))}
              </FormGroup>
            </div>
          )}
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 10 }}
          >
            Your earnings are not affected if the rider did not provide
            in-person payment.
          </Typography>
        </>
      )}
    </div>
  );
};

const getPaymentTypeLabel = (paymentType, costCenter) => {
  if (paymentType === "rider_pass") return costCenter?.name || "Ride Pass";
  if (paymentType === "vouchers") return costCenter?.name || "Vouchers";
  if (paymentType === "cash") return "Cash";
  if (paymentType === "cash_and_vouchers") return "Cash and Vouchers";
  return paymentType;
};

const DetailRow = ({ label, children }) => (
  <div style={{ display: "flex", gap: "5px", alignItems: "baseline" }}>
    <p style={{ flexShrink: 0, whiteSpace: "nowrap", width: "120px" }}>
      {label}:
    </p>
    <FontWeight>{children}</FontWeight>
  </div>
);

const ReasonPill = ({ children }) => (
  <span
    style={{
      background: "#f5f5f5",
      border: "1px solid #d6d6d6",
      borderRadius: "5px",
      display: "inline-flex",
      fontSize: "12px",
      fontWeight: 600,
      lineHeight: "18px",
      margin: "4px 5px 0 0",
      padding: "2px 8px",
      width: "fit-content",
    }}
  >
    {children}
  </span>
);

export const InPersonPaymentDisplay = ({ inPersonPayment, costCenter = null }) => {
  if (!inPersonPayment) return null;

  const {
    paymentCollected,
    paymentType,
    cashAmountCents,
    voucherAmountCents,
    voucherCount,
    paymentUncollectedReasons,
  } = inPersonPayment;

  const selectedReasons = paymentUncollectedReasons?.filter(
    (reason) => reason.value !== false
  );

  return (
    <div
      style={{
        borderTop: "1px solid lightgrey",
        marginTop: "14px",
        paddingTop: "14px",
      }}
    >
      <p
        style={{
          fontSize: "1rem",
          marginBottom: "6px",
        }}
      >
        IN-PERSON PAYMENT
      </p>
      <DetailRow label="Collected">{paymentCollected ? "Yes" : "No"}</DetailRow>

      {paymentType ? (
        <DetailRow label="Type">
          {getPaymentTypeLabel(paymentType, costCenter)}
        </DetailRow>
      ) : null}

      {paymentCollected ? (
        <>
          {cashAmountCents != null ? (
            <DetailRow label="Cash">
              {formatCentsAsCurrency(cashAmountCents)}
            </DetailRow>
          ) : null}
          {voucherAmountCents != null ? (
            <DetailRow label="Vouchers">
              {formatCentsAsCurrency(voucherAmountCents)}
            </DetailRow>
          ) : null}
          {voucherCount != null ? (
            <DetailRow label="Voucher Count">{voucherCount}</DetailRow>
          ) : null}
        </>
      ) : selectedReasons?.length > 0 ? (
        <div style={{ display: "flex", gap: "5px", alignItems: "flex-start" }}>
          <p style={{ width: "82px" }}>Reasons:</p>
          <div style={{ display: "flex", flexWrap: "wrap", maxWidth: "380px" }}>
            {selectedReasons.map((reason) => (
              <ReasonPill key={reason.slug}>
                {reason.label || reason.slug}
              </ReasonPill>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const InPersonPaymentSection = ({
  inPersonPaymentOptions,
  costCenter = null,
  riderFareCents = null,
  uncollectedReasons,
  passShown,
  setPassShown,
  passUncollectedReasons,
  setPassUncollectedReasons,
  paymentCollected,
  setPaymentCollected,
  cashAmountCents,
  setCashAmountCents,
  voucherAmountCents,
  setVoucherAmountCents,
  voucherCount,
  setVoucherCount,
  cashVoucherUncollectedReasons,
  setCashVoucherUncollectedReasons,
}) => {
  return (
    <>
      {inPersonPaymentOptions.inPersonPassEnabled && (
        <RidePassSection
          passShown={passShown}
          setPassShown={setPassShown}
          selectedReasons={passUncollectedReasons}
          setSelectedReasons={setPassUncollectedReasons}
          uncollectedReasons={uncollectedReasons}
          passName={costCenter?.name}
        />
      )}
      {(inPersonPaymentOptions.inPersonCashEnabled ||
        inPersonPaymentOptions.inPersonVouchersEnabled) && (
        <CashVoucherSection
          paymentCollected={paymentCollected}
          setPaymentCollected={setPaymentCollected}
          cashAmountCents={cashAmountCents}
          setCashAmountCents={setCashAmountCents}
          voucherAmountCents={voucherAmountCents}
          setVoucherAmountCents={setVoucherAmountCents}
          voucherCount={voucherCount}
          setVoucherCount={setVoucherCount}
          selectedReasons={cashVoucherUncollectedReasons}
          setSelectedReasons={setCashVoucherUncollectedReasons}
          inPersonPaymentOptions={inPersonPaymentOptions}
          uncollectedReasons={uncollectedReasons}
          costCenter={costCenter}
          riderFareCents={riderFareCents}
        />
      )}
    </>
  );
};

export default InPersonPaymentSection;
